import { useHistory, useRouteMatch } from 'react-router-dom';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { Separator } from '@givebutter/primitives/separator';
import { PrimaryBadge } from 'assets/js/pages/contacts/PrimaryBadge';
import { getContactFormQueryParams } from 'assets/js/pages/contacts/contactFormParams';
import { CopyButton } from 'components/copy-button';
import { TruncatedText } from 'components/truncated-text';
import { EmptyData } from 'components/util';
import { useCurrentContact } from 'customHooks/useCurrentContact';
import { getFormattedPhoneNumber } from 'helpers/formatting';
import { capitalize } from 'lodash';
import { twMerge } from 'tailwind-merge';
import { Contact } from 'types/domain';

const CompanyDetails = () => {
    const history = useHistory();
    const match = useRouteMatch();
    const { data: contact } = useCurrentContact();

    return (
        <Box className={'flex flex-col'}>
            <div className={'px-6 py-4'}>
                <p className={'text-lg font-medium text-gray-900'}>Profile Information</p>
            </div>
            <Separator />
            <div className={'flex flex-col gap-3 px-6 py-3'}>
                <div className={'flex items-start justify-between gap-2'}>
                    <div className={'flex gap-10'}>
                        <div className={'flex flex-col'}>
                            <p className={'text-sm text-gray-600'}>Name</p>
                            <p className={'flex text-base'}>{contact.company_name}</p>
                        </div>
                    </div>
                    <Button
                        variant="none"
                        color="primary"
                        size="sm"
                        className="font-medium"
                        onClick={() =>
                            history.push({
                                pathname: `${match.url}/details/edit`,
                                search: getContactFormQueryParams({ autoFocus: 'companyName' }),
                            })
                        }
                    >
                        Edit
                    </Button>
                </div>
                <Separator className={'!-mx-6 !w-auto'} />
                <div className={'flex items-start justify-between gap-2'}>
                    <div className={'flex flex-col'}>
                        <p className={'text-sm text-gray-600'}>Email</p>
                        <div className={'flex flex-col gap-1'}>
                            {contact.emails && contact.emails.length !== 0 ? (
                                contact.emails.map((email, index) => (
                                    <div
                                        className={'flex items-center gap-2'}
                                        key={`${email}-${index}`}
                                    >
                                        <div className={'flex max-w-[430px] items-center'}>
                                            <TruncatedText>
                                                <p className={'text-base'}>{email.value}</p>
                                            </TruncatedText>
                                            <CopyButton text={email.value} />
                                        </div>
                                        <p className={'rounded-full bg-gray-50 px-2 py-1 text-xs'}>
                                            {capitalize(email.type)}
                                        </p>
                                        {email.value === contact.primary_email && <PrimaryBadge />}
                                    </div>
                                ))
                            ) : (
                                <Empty className={'self-start'} />
                            )}
                        </div>
                    </div>
                    <Button
                        variant="none"
                        color="primary"
                        size="sm"
                        className="font-medium"
                        onClick={() =>
                            history.push({
                                pathname: `${match.url}/details/edit`,
                                search: getContactFormQueryParams({
                                    only: ['emails'],
                                    autoFocus: 'emails.0.value',
                                }),
                            })
                        }
                    >
                        Edit
                    </Button>
                </div>
                <Separator className={'!-mx-6 !w-auto'} />
                <div className={'flex items-start justify-between gap-2'}>
                    <div className={'flex flex-col'}>
                        <p className={'text-sm text-gray-600'}>Phone</p>
                        <div className={'flex flex-col gap-1'}>
                            {contact.phones && contact.phones.length !== 0 ? (
                                [...contact.phones].map((phone, index) => (
                                    <div
                                        className={'flex items-center gap-2'}
                                        key={`${phone}-${index}`}
                                    >
                                        <div className={'flex items-center'}>
                                            <p className={'text-base'}>
                                                {getFormattedPhoneNumber(phone.value)}
                                            </p>
                                            <CopyButton text={phone.value} />
                                        </div>
                                        <p className={'rounded-full bg-gray-50 px-2 py-1 text-xs'}>
                                            {capitalize(phone.type)}
                                        </p>
                                        {phone.value === contact.primary_phone && <PrimaryBadge />}
                                    </div>
                                ))
                            ) : (
                                <Empty className={'self-start'} />
                            )}
                        </div>
                    </div>
                    <Button
                        variant="none"
                        color="primary"
                        size="sm"
                        className="font-medium"
                        onClick={() =>
                            history.push({
                                pathname: `${match.url}/details/edit`,
                                search: getContactFormQueryParams({
                                    only: ['phones'],
                                    autoFocus: 'phones.0.value',
                                }),
                            })
                        }
                    >
                        Edit
                    </Button>
                </div>
                <Separator className={'!-mx-6 !w-auto'} />
                <div className={'flex items-start justify-between gap-2'}>
                    <div className={'flex flex-col overflow-auto'}>
                        <p className={'text-sm text-gray-600'}>Note</p>
                        <p className={'flex text-base'}>
                            {contact.note ? contact.note : <Empty />}
                        </p>
                    </div>
                    <Button
                        variant="none"
                        color="primary"
                        size="sm"
                        className="font-medium"
                        onClick={() =>
                            history.push({
                                pathname: `${match.url}/details/edit`,
                                search: getContactFormQueryParams({ autoFocus: 'note' }),
                            })
                        }
                    >
                        Edit
                    </Button>
                </div>
            </div>
        </Box>
    );
};

const Empty = ({ className = '' }) => {
    return <EmptyData className={twMerge('mt-1.5', className)} />;
};

const getFullContactName = (contact: Contact) =>
    [contact.prefix, contact.first_name, contact.middle_name, contact.last_name, contact.suffix]
        .filter(prop => !!prop)
        .join(' ');

export { CompanyDetails };
